import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { theme, UserAvatar } from '@side/pantry';
import { useSearchParams } from 'next/navigation';
import { useAgentTeamId } from '@/utils/use-agent-team-id';
import { UploadImageModal } from '../UploadImageModal';
import { useAccountSettingsLogic } from './useAccountSettingsLogic';

export const AccountSettingsMobileHeader = () => {
  const searchParams = useSearchParams();
  const { agentTeamId, isLoading: agentTeamIdLoading } = useAgentTeamId();
  const teamId = searchParams?.get('team') ?? agentTeamId;

  const {
    user,
    isLoading: userinfoLoading,
    name,
    teams,
    avatarEditModalOpen,
    setAvatarEditModalOpen,
    loadingUpdateAvatar,
    onSubmitAvatar,
  } = useAccountSettingsLogic();

  // agentTeamId comes from a second (team-members) call that starts only after
  // userinfo returns, so coalesce their loading states — otherwise "Welcome,
  // {name}" shows while selectedTeamName is still an empty string.
  const userQueryLoading = userinfoLoading || agentTeamIdLoading;

  const selectedTeam = teams.find((team) => team?.id === teamId);
  const selectedTeamName = selectedTeam?.alias ?? '';

  return (
    <>
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <UserAvatar
            name={name}
            avatarUrl={user?.photoUrl || ''}
            sx={{ height: { xs: 70, lg: 50 }, width: { xs: 70, lg: 50 } }}
          />
          <IconButton
            aria-label="Edit avatar"
            onClick={() => setAvatarEditModalOpen(true)}
            sx={{
              position: 'absolute',
              bottom: { xs: 0, lg: 5 },
              right: { xs: 0, lg: 5 },
              backgroundColor: 'primary.main',
              color: 'common.white',
              width: theme.spacing(3),
              height: theme.spacing(3),
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: theme.spacing(2) }} />
          </IconButton>
        </Box>
        <Stack sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">
            {!name || userQueryLoading
              ? 'Welcome'
              : `Welcome, ${user?.firstName}`}
          </Typography>
          <Typography
            variant="subtitle1"
            data-testid="selected-team-name"
            sx={{
              color: 'text.secondary',
            }}
          >
            {selectedTeamName}
          </Typography>
        </Stack>
      </Stack>
      <UploadImageModal
        key={user?.photoUrl || 'new'}
        title="Select a photo"
        headline={
          <>
            Upload a new photo for <b>{name}</b>
          </>
        }
        open={avatarEditModalOpen}
        onClose={() => setAvatarEditModalOpen(false)}
        onSubmit={onSubmitAvatar}
        uploading={loadingUpdateAvatar}
        imageUrl={user?.photoUrl || ''}
      />
    </>
  );
};
